import { useCallback } from 'react'

import { useApolloClient } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { useSavedViewTracking } from '~publish/hooks/useSavedViewTracking'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import { writeSavedViewsList } from './savedViewsCache'
import type { SavedView } from './useSavedViewsQuery'
import { missingOrganizationOutcome } from './utils'
import { ReorderSavedViewsMutation } from '../graphql/ReorderSavedViewsMutation'
import { SavedViewsQuery } from '../graphql/SavedViewsQuery'

/**
 * Reorder `views` so they match `viewIds`. Returns `null` when any id in
 * `viewIds` has no backing view in `views` — better to skip the optimistic
 * write entirely than to flash a truncated list before the server resyncs.
 */
function reorderByIds(
  views: SavedView[],
  viewIds: string[],
): SavedView[] | null {
  const byId = new Map(views.map((view) => [view.id, view]))
  const reordered: SavedView[] = []
  for (const id of viewIds) {
    const view = byId.get(id)
    if (!view) return null
    reordered.push(view)
  }
  return reordered
}

type ReorderSavedViewsMutationResult = ReturnType<
  typeof useTypedMutation<
    'SavedViewsResponse',
    ResultOf<typeof ReorderSavedViewsMutation>['reorderSavedViews'],
    ResultOf<typeof ReorderSavedViewsMutation>,
    VariablesOf<typeof ReorderSavedViewsMutation>
  >
>

type UseReorderSavedViewsMutationResult = readonly [
  (
    viewIds: Array<SavedView['id']>,
  ) => Promise<Awaited<ReturnType<ReorderSavedViewsMutationResult[0]>>>,
  ReorderSavedViewsMutationResult[1],
]

/**
 * Reorder saved views. Writes the new order to the cache optimistically so
 * the sidebar flips immediately on drop; Apollo rolls the optimistic write
 * back on error, and `refetchQueries` then resyncs against the server. The
 * `update` callback runs against both the optimistic and the real payload,
 * so the cache rewrite logic doesn't have to branch.
 */
export function useReorderSavedViewsMutation(): UseReorderSavedViewsMutationResult {
  const organizationId = useCurrentOrganization()?.id
  const client = useApolloClient()
  const { trackReordered } = useSavedViewTracking()

  const [mutate, result] = useTypedMutation(
    ReorderSavedViewsMutation,
    (data) => data.reorderSavedViews,
    {
      successTypename: 'SavedViewsResponse',
      refetchQueries: [SavedViewsQuery],
      awaitRefetchQueries: true,
    },
  )

  const reorderSavedViews = useCallback(
    async (viewIds: Array<SavedView['id']>) => {
      if (!organizationId) return missingOrganizationOutcome()

      // Build the optimistic payload from whatever the cache currently holds
      // so the subscribed SavedViewsQuery flips to the new order while the
      // mutation is in flight. When the cache is empty (rare — only if the
      // sidebar hasn't loaded the list yet) we just skip the optimistic
      // step and let refetchQueries do the work.
      const cached = client.readQuery({
        query: SavedViewsQuery,
        variables: { input: { organizationId } },
      })
      const optimisticOrdered = cached
        ? reorderByIds(cached.savedViews.savedViews, viewIds)
        : null

      const outcome = await mutate({
        variables: {
          input: { organizationId, viewIds },
        },
        ...(optimisticOrdered && {
          optimisticResponse: {
            reorderSavedViews: {
              __typename: 'SavedViewsResponse' as const,
              savedViews: optimisticOrdered,
            },
          },
        }),
        update: (cache, { data }) => {
          if (!isOfType(data?.reorderSavedViews, 'SavedViewsResponse')) return

          const reorderedViews = data.reorderSavedViews.savedViews
          writeSavedViewsList(cache, organizationId, () => reorderedViews)
        },
      })

      if (outcome.success) {
        // Track the server-confirmed order from the response rather than
        // the request payload: the backend canonicalises ids (drops
        // unknowns, may normalise gaps) so the analytics event should
        // reflect the persisted order, not what was submitted.
        trackReordered(outcome.data.savedViews.map((v) => v.id))
      } else {
        toast.error(outcome.error.message)
      }

      return outcome
    },
    [client, mutate, organizationId, trackReordered],
  )

  return [reorderSavedViews, result] as const
}
